import { DbKey, WithDbId } from '../adl-gen/common/db';
import { AppClient, ApplicationRole, AppUser } from '../adl-gen/totalinteraction/futurefit/db';
import { NotEnabled } from '../adl-gen/totalinteraction/futurefit/requests';
import { AppClientConfig } from '../adl-gen/totalinteraction/futurefit/uiconfig';
import { isLoading, loadedValue, loadingError, MaybeLoading } from '../loader/loader';
import { Service } from '../service/service';

// tslint:disable: interface-over-type-literal

export type LoggedIn = {
  kind: 'logged-in';
  appUserWithDbId: WithDbId<AppUser>;
  appClient: AppClientConfig;
  role: ApplicationRole;
  isManager: boolean;
};

export type LoggedInSuper = {
  kind: 'super-logged-in';
  appUserWithDbId: WithDbId<AppUser>;
  appClient: AppClientConfig;
  appClientList: WithDbId<AppClient>[];
};

export function loggedIn(args: {
  appUserWithDbId: WithDbId<AppUser>;
  appClient: AppClientConfig;
  role: ApplicationRole;
  isManager: boolean;
}): LoggedIn {
  return { ...args, kind: 'logged-in' };
}

export function loggedInSuper(args: {
  appUserWithDbId: WithDbId<AppUser>;
  appClient: AppClientConfig;
  appClientList: WithDbId<AppClient>[];
}): LoggedInSuper {
  return { ...args, kind: 'super-logged-in' };
}

export type Unregistered = {
  kind: 'unregistered';
  reason: NotEnabled;
};

export function unregistered(reason: NotEnabled): Unregistered {
  return {
    kind: 'unregistered',
    reason
  };
}

export type NotLoggedIn = {
  kind: 'not-logged-in';
};

export function notLoggedIn(): NotLoggedIn {
  return { kind: 'not-logged-in' };
}

export type MaybeLoggedIn = LoggedIn | LoggedInSuper | NotLoggedIn | Unregistered;

/**
 * Data and actions related to user identity:   (stored state moved to appStore)
 */
export class IdentityService {
  constructor(
    private readonly service: Service,
    private readonly appClient: AppClientConfig,
    private readonly setIdentity: (identity: MaybeLoading<MaybeLoggedIn>, context?: string) => void
  ) { }

  async init(): Promise<void> {
    const context = 'identityService:init';
    this.setIdentity(isLoading(), context);
    try {
      /// Use Auth0 token saved after handleAuthentication
      // Although no call to server may yet have been made, this should work, because every API call goes through
      // AccessManager on the server, and as every call must include a valid X_AUTH_TOKEN header (which will be set
      // by handleAuthentication (I think) or login), this will populate the token on the server before
      // the checkAuth call is executed. Unless the user has not logged in of course

      // tslint:disable-next-line: no-console
      console.debug(context, "Calling checkAuth");
      const appUserId: DbKey<AppUser> | null = await this.service.checkAuth();
      if (appUserId === null) {
        this.setIdentity(loadedValue(notLoggedIn()), context + ':post-checkAuth');
      } else {
        /// Use Auth0 token saved after handleAuthentication, but return a Server-issued token
        const resp = await this.service.userRegisterLogin();

        if (resp.kind === 'disallowed') {
          this.setIdentity(loadedValue(unregistered(resp.value)), context + ':post-registration');
        } else {
          // tslint:disable-next-line: no-console
          console.debug(context, "Setting token");

          /// Set Server-issued token (overwrites Auth0 token not longer needed)
          this.service.tokenManager.setToken(resp.value.accessToken);

          switch (resp.kind) {
            case 'super':
              this.setIdentity(
                loadedValue(
                  loggedInSuper({
                    appUserWithDbId: resp.value.appUser,
                    appClient: this.appClient,
                    appClientList: resp.value.appClients
                  })
                ), context + ':post-registration(super)'
              );
              break;
            case 'admin':
              this.setIdentity(
                loadedValue(
                  loggedIn({
                    appUserWithDbId: resp.value.appUser,
                    appClient: this.appClient,
                    role: ApplicationRole.admin,
                    isManager: resp.value.isManager
                  })
                ), context + ':post-registration(admin)'
              );
              break;
            default:
              // user
              this.setIdentity(
                loadedValue(
                  loggedIn({
                    appUserWithDbId: resp.value.appUser,
                    appClient: this.appClient,
                    role: ApplicationRole.user,
                    isManager: resp.value.isManager
                  })
                ), context + ':post-registration(user)'
              );
          }
        }
      }
    } catch (err) {
      this.setIdentity(loadingError(`${err}`), context);
    }
  }
}



// WEBPACK FOOTER //
// ./src/stores/identity-store.ts